import { Component } from '@theme/component';

/**
 * Wraps the collection filter/sort bar and reflects whether it has become
 * pinned to the top of the viewport (vs. sitting in normal document flow) as
 * a `data-stuck` attribute, so CSS can animate in a background/shadow only
 * once the bar is actually floating over the product grid.
 *
 * @typedef {object} CarilohaStickyFiltersRefs
 * @property {HTMLElement} stickySentinel - Zero-height marker rendered just above the sticky bar.
 *
 * @extends {Component<CarilohaStickyFiltersRefs>}
 */
class CarilohaStickyFiltersComponent extends Component {
  requiredRefs = ['stickySentinel'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {Element | null} */
  #observedSentinel = null;

  connectedCallback() {
    super.connectedCallback();
    this.#observeSentinel();
  }

  /**
   * Re-attaches the observer after the Section Rendering API morphs this
   * block's content (e.g. after a facet filter is applied), in case the
   * sentinel element itself was replaced.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#observeSentinel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
  }

  #observeSentinel() {
    const { stickySentinel } = this.refs;
    if (!stickySentinel || stickySentinel === this.#observedSentinel) return;

    if (!this.#observer) {
      this.#observer = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        this.toggleAttribute('data-stuck', !entry.isIntersecting);
      });
    } else {
      this.#observer.disconnect();
    }

    this.#observedSentinel = stickySentinel;
    this.#observer.observe(stickySentinel);
  }
}

if (!customElements.get('cariloha-sticky-filters')) {
  customElements.define('cariloha-sticky-filters', CarilohaStickyFiltersComponent);
}
